// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePageSize from "@/hooks/usePageSize";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TySubscription } from "@/types";
import { formatMoney, readLangValue } from "@/utils";
import SubscriptionCard from "@/UI/cards/SubscriptionCard";
import ActionsBar, { type TyBarTab } from "@/UI/components/ActionsBar";
import ListPanel from "@/UI/components/ListPanel";
import Paginator from "@/UI/components/Paginator";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import AddStoragePopup from "@/UI/popups/AddStoragePopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import ConfirmPaymentPopup from "@/UI/popups/ConfirmPaymentPopup";
import SubscriptionFormPopup from "@/UI/popups/SubscriptionFormPopup";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "pending" | "storage" | "live" | "ending" | "history";

/**
 * What each scope asks the endpoint for. A tab is a different list rather than a narrowing of one —
 * and "ending" is a window on `endsAt` instead of a status, because running out is a date.
 */
const TAB_QUERY: Record<TyTab, Record<string, string>> = {
    pending: { status: "pending" },
    /**
     * A **pack** waiting on a transfer, not a period. The row itself is live, so no status filter
     * would ever surface it — which is why this is its own scope rather than a chip on `pending`.
     */
    storage: { storagePending: "1" },
    live: { status: "active,coming" },
    ending: { status: "active,grace", endingWithin: String(constants.endingSoonDays) },
    history: { status: "ended,cancelled" },
};

/**
 * The billing queue. **Not a report** — billing is manual, so every row in the first tab is a decision
 * somebody has to take: confirm this transfer, or let the nightly job cancel it.
 *
 * `pending` leads because it is the only scope with a deadline attached. A period has no screen of its
 * own, so opening a row goes to the company it belongs to, where its periods are stacked in order.
 */
export default function SubscriptionsScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const addComp = usePopup((state) => state.addComp);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const tab = (params.get("tab") as TyTab) || "pending";
    const search = params.get("search") ?? "";
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);

    const subscriptions = useFetch<{ subscriptions: TySubscription[]; total: number }>("/subscriptions", {}, false);
    /** How many are waiting, whichever tab is open — the subtitle is about the queue, not the page. */
    const pending = useFetch<{ total: number }>("/subscriptions", {}, false);
    /** Row actions share one call: only one can be in flight, and each sets its own path. */
    const action = useFetch("/subscriptions", {}, false);

    const load = () => {
        subscriptions.invoke({ query: { ...TAB_QUERY[tab], search: search.trim() || undefined, page, limit: pageSize } });
        pending.invoke({ query: { status: "pending", limit: 1 } });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, search, page, pageSize]);

    useEffect(() => {
        if (!action.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        // Narrowing a list — or changing tab — puts you back at its start.
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const rows = subscriptions.data?.subscriptions ?? [];
    const tabs: TyBarTab[] = (Object.keys(TAB_QUERY) as TyTab[]).map((key) => ({ key, label: t.subscriptions.tabs[key] }));

    const nameOf = (row: TySubscription) => readLangValue(row.organization.name, lang) || row.organization.slug;

    /** The packs a company has asked for and not transferred for, as one figure: they were asked
        for in a single breath and priced as one transfer. */
    const storageAsked = (row: TySubscription) => {
        const waiting = row.addons.filter((addon) => addon.status === "pending");
        if (!waiting.length) return null;
        return {
            count: waiting.length,
            gb: waiting.reduce((total, addon) => total + addon.storageGb, 0),
            amount: waiting.reduce((total, addon) => total + addon.amount, 0),
        };
    };

    const openConfirmStorage = (row: TySubscription) => {
        const asked = storageAsked(row);
        if (!asked) return;
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.subscriptions.confirmStorage,
                message: t.subscriptions.confirmStorageMessage
                    .replace("{amount}", formatMoney(asked.amount))
                    .replace("{name}", nameOf(row))
                    .replace("{count}", String(asked.count))
                    .replace("{gb}", String(asked.gb)),
                confirmLabel: t.subscriptions.confirmStorage,
                danger: false,
                onConfirm: () => action.invoke({ method: "POST", path: `/subscriptions/${row._id}/storage/confirm` }),
            },
        });
    };

    const openConfirm = (row: TySubscription) =>
        addComp({
            name: "confirm-payment",
            Component: ConfirmPaymentPopup,
            anim: "scale",
            details: { subscription: row, onDone: load },
        });

    const openActions = (row: TySubscription, anchor: DOMRect) => {
        const isCancellable = row.status === "pending" || row.status === "coming";
        const isLive = ["active", "grace", "coming"].includes(row.status);

        const actions: TyAction[] = [
            {
                key: "organization",
                label: t.screens.organization,
                icon: "building",
                onSelect: () => navigate(`/organizations/${row.organization.slug}?tab=subscription`),
            },
            ...(row.status === "pending"
                ? [{ key: "confirm", label: t.subscriptions.confirmPayment, icon: "check" as const, onSelect: () => openConfirm(row) }]
                : []),
            ...(storageAsked(row)
                ? [{ key: "confirm-storage", label: t.subscriptions.confirmStorage, icon: "drive" as const, onSelect: () => openConfirmStorage(row) }]
                : []),
            ...(isLive
                ? [
                      {
                          key: "storage",
                          label: t.subscriptions.addStorage,
                          icon: "drive" as const,
                          onSelect: () =>
                              addComp({
                                  name: "add-storage",
                                  Component: AddStoragePopup,
                                  anim: "scale",
                                  details: { subscription: row, onDone: load },
                              }),
                      },
                  ]
                : []),
            // A live period runs to its end — only a request or a queued renewal can be called off.
            ...(isCancellable
                ? [
                      {
                          key: "cancel",
                          label: t.subscriptions.cancelRequest,
                          icon: "close" as const,
                          danger: true,
                          onSelect: () =>
                              addComp({
                                  name: "confirm",
                                  Component: ConfirmPopup,
                                  anim: "scale",
                                  details: {
                                      title: t.subscriptions.cancelRequest,
                                      message: t.subscriptions.confirmCancelRequest.replace("{name}", nameOf(row)),
                                      confirmLabel: t.subscriptions.cancelRequest,
                                      onConfirm: () => action.invoke({ method: "POST", path: `/subscriptions/${row._id}/cancel` }),
                                  },
                              }),
                      },
                  ]
                : []),
        ];

        addComp({
            name: "subscription-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    const emptyMessage =
        tab === "pending"
            ? t.subscriptions.emptyPending
            : tab === "storage"
              ? t.subscriptions.emptyStorage
              : tab === "ending"
                ? t.subscriptions.emptyEnding.replace("{days}", String(constants.endingSoonDays))
                : t.subscriptions.empty;

    return (
        <section className="list-screen">
            <ListPanel
                title={t.subscriptions.title}
                subtitle={t.subscriptions.subtitle
                    .replace("{pending}", String(pending.data?.total ?? 0))
                    .replace("{total}", String(subscriptions.data?.total ?? 0))}
                /* The deadline is why this tab is first, so it is stated once here rather than on
                   every row that has one. */
                note={
                    tab === "pending"
                        ? t.subscriptions.pendingHint.replace("{days}", String(constants.pendingPaymentWindowDays))
                        : tab === "storage"
                          ? t.subscriptions.storageRequestHint
                          : undefined
                }
            >
                <ActionsBar
                    tabs={tabs}
                    activeTab={tab}
                    onTab={(key) => apply({ tab: key === "pending" ? null : key })}
                    addLabel={t.subscriptions.recordSubscription}
                    onAdd={() =>
                        addComp({
                            name: "subscription-form",
                            Component: SubscriptionFormPopup,
                            anim: "scale",
                            details: { onDone: load },
                        })
                    }
                    isFilterOpen={isFiltering}
                    onFilterOpen={() => {
                        setDraftSearch(search);
                        setIsFiltering(true);
                    }}
                    hasFilters={!!search}
                    onFilterApply={() => {
                        setIsFiltering(false);
                        apply({ search: draftSearch.trim() || null });
                    }}
                    onFilterClear={() => {
                        setDraftSearch("");
                        setIsFiltering(false);
                        apply({ search: null });
                    }}
                    onFilterCancel={() => setIsFiltering(false)}
                    filterContent={
                        <FilterSearchInput
                            value={draftSearch}
                            onChange={setDraftSearch}
                            label={t.common.search}
                            placeholder={t.subscriptions.searchPlaceholder}
                            onEnter={() => {
                                setIsFiltering(false);
                                apply({ search: draftSearch.trim() || null });
                            }}
                        />
                    }
                />
            </ListPanel>

            {action.error && <p className="screen-state screen-state--error">{action.error}</p>}
            {subscriptions.loading && <p className="screen-state">{t.common.loading}</p>}
            {subscriptions.error && <p className="screen-state screen-state--error">{subscriptions.error}</p>}
            {!subscriptions.loading && !subscriptions.error && !rows.length && <p className="screen-state">{emptyMessage}</p>}

            <div className="list-screen__rows">
                {rows.map((row) => (
                    <SubscriptionCard
                        key={row._id}
                        subscription={row}
                        onOpen={() => navigate(`/organizations/${row.organization.slug}?tab=subscription`)}
                        onConfirm={row.status === "pending" ? () => openConfirm(row) : undefined}
                        onConfirmStorage={storageAsked(row) ? () => openConfirmStorage(row) : undefined}
                        onActions={(anchor) => openActions(row, anchor)}
                    />
                ))}
            </div>

            <Paginator
                page={page}
                total={subscriptions.data?.total ?? 0}
                count={rows.length}
                onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
            />
        </section>
    );
}
